import { useCallback, useEffect, useState } from 'react';
import { analyticsClient } from '../../utils/analytics/client';

interface BrandingData {
  logo?: string;
}

export type { BrandingData };

const resizeImage = (file: File): Promise<File> => {
  return new Promise((resolve, reject) => {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const img = new Image();

    img.onload = () => {
      if (!ctx) {
        reject(new Error('Could not get canvas context'));
        return;
      }

      // Calculate scaling to fit the larger dimension at 256px
      const scale = Math.min(256 / img.width, 256 / img.height);
      const scaledWidth = Math.round(img.width * scale);
      const scaledHeight = Math.round(img.height * scale);

      // Set canvas size to the scaled dimensions (not fixed 256x256)
      canvas.width = scaledWidth;
      canvas.height = scaledHeight;

      // Draw the resized image without padding
      ctx.drawImage(img, 0, 0, scaledWidth, scaledHeight);

      // Convert to blob and then to file
      canvas.toBlob(
        (blob) => {
          if (!blob) {
            reject(new Error('Could not create blob'));
            return;
          }

          // Use a standardized filename for the scaled logo
          const ext = file.type.split('/')[1] || 'png';
          const resizedFile = new File([blob], `logo.${ext}`, {
            type: file.type,
            lastModified: Date.now(),
          });

          resolve(resizedFile);
        },
        file.type,
        0.9,
      ); // 0.9 quality for good compression
    };

    img.onerror = () => {
      reject(new Error('Could not load image'));
    };

    img.src = URL.createObjectURL(file);
  });
};

export function useLogoUpload(brandingData: BrandingData) {
  const [isCropImageOpen, setIsCropImageOpen] = useState<boolean>(false);
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [logoPreview, setLogoPreview] = useState<string | null>(null);
  const [prevLogoFile, setPrevLogoFile] = useState<File | null>(null);
  const [prevLogoPreview, setPrevLogoPreview] = useState<string | null>(null);
  const [originalLogoFile, setOriginalLogoFile] = useState<File | null>(null);
  const [originalLogoPreview, setOriginalLogoPreview] = useState<string | null>(
    null,
  );
  const [currentFileType, setCurrentFileType] = useState<string>('');

  useEffect(() => {
    if (brandingData) {
      setLogoPreview(brandingData.logo || null);
      setLogoFile(null);
    }
  }, [brandingData]);

  const onDrop = useCallback(
    (acceptedFiles: File[]) => {
      if (acceptedFiles[0]) {
        const file = acceptedFiles[0];
        setPrevLogoFile(logoFile);
        setPrevLogoPreview(logoPreview);
        setOriginalLogoFile(file);
        setOriginalLogoPreview(URL.createObjectURL(file));
        setLogoFile(file);
        setLogoPreview(URL.createObjectURL(file));
        setCurrentFileType(file.type);
        setIsCropImageOpen(true);

        // Track analytics when logo is uploaded to crop tool
        analyticsClient.track('branding_logo_uploaded_to_crop', {
          fileType: file.type,
          fileSize: file.size,
        });
      }
    },
    [logoFile, logoPreview],
  );

  const handleCropSave = (blob: Blob) => {
    const ext = currentFileType.split('/')[1] || 'png';
    const file = new File([blob], `logo.${ext}`, { type: blob.type });
    setLogoFile(file);
    setLogoPreview(URL.createObjectURL(file));
    setIsCropImageOpen(false);
  };

  // Function to get the scaled version of the current logo file for backend upload
  const getScaledLogoFile = useCallback(async (): Promise<File | null> => {
    if (!logoFile) return null;

    try {
      return await resizeImage(logoFile);
    } catch (error) {
      console.error('Error scaling image:', error);
      return logoFile; // Fallback to original file if scaling fails
    }
  }, [logoFile]);

  return {
    isCropImageOpen,
    setIsCropImageOpen,
    logoFile,
    setLogoFile,
    logoPreview,
    setLogoPreview,
    prevLogoFile,
    prevLogoPreview,
    originalLogoFile,
    originalLogoPreview,
    currentFileType,
    setCurrentFileType,
    onDrop,
    handleCropSave,
    getScaledLogoFile,
  };
}
